import {
    generateChecklist,
    refineChecklist,
} from "@/api/clause-checklists/checklist-generation";
import {
    GenerationEvent,
    GenerationState,
    IChecklistGenerationTargets,
    IEventLogEntry,
    IRefinementDiff,
} from "@/api/clause-checklists/checklist-generation.types";
import { parseNDJSONStream } from "@/utils/events";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

export const useChecklistGeneration = () => {
    const [state, setState] = useState<GenerationState>("idle");
    const [eventLog, setEventLog] = useState<IEventLogEntry[]>([]);
    const [draftId, setDraftId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [errorStatus, setErrorStatus] = useState<number | null>(null);
    const [pendingDiff, setPendingDiff] = useState<IRefinementDiff | null>(
        null
    );
    // Preserved on reset so "Start Over" can repopulate the prompt input
    const [lastPrompt, setLastPrompt] = useState("");
    const [lastOrganizationId, setLastOrganizationId] = useState<
        number | null | undefined
    >();
    const [lastOrganizationGroupId, setLastOrganizationGroupId] = useState<
        number | null | undefined
    >();
    const abortControllerRef = useRef<AbortController | null>(null);
    // Track iteration from block_start events so non-start events get correct iteration
    const currentIterationRef = useRef(0);

    // Abort in-flight stream on unmount to prevent state updates on unmounted component
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const processEvent = useCallback((event: GenerationEvent) => {
        switch (event.type) {
            case "block_start": {
                currentIterationRef.current = event.iteration;
                if (event.block.type === "tool_use") {
                    setEventLog((prev) => [
                        ...prev,
                        {
                            index: event.index,
                            iteration: event.iteration,
                            type: "tool_use",
                            content: event.block.loading_text ?? "",
                            toolName: event.block.name,
                        },
                    ]);
                } else if (event.block.type === "thinking") {
                    setEventLog((prev) => [
                        ...prev,
                        {
                            index: event.index,
                            iteration: event.iteration,
                            type: "thinking",
                            content: "",
                        },
                    ]);
                }
                break;
            }

            case "block_delta": {
                if (event.delta.type === "thinking_delta") {
                    const thinkingText = event.delta.thinking;
                    const iteration = currentIterationRef.current;
                    setEventLog((prev) => {
                        // Find an existing thinking entry for this block index
                        const existingIdx = prev.findIndex(
                            (e) =>
                                e.index === event.index && e.type === "thinking"
                        );
                        if (existingIdx !== -1) {
                            const updated = [...prev];
                            updated[existingIdx] = {
                                ...updated[existingIdx],
                                content:
                                    updated[existingIdx].content + thinkingText,
                            };
                            return updated;
                        }
                        return [
                            ...prev,
                            {
                                index: event.index,
                                iteration,
                                type: "thinking",
                                content: thinkingText,
                            },
                        ];
                    });
                }
                break;
            }

            case "block_complete": {
                const { block } = event;
                if (block.type === "tool_result") {
                    setEventLog((prev) => [
                        ...prev,
                        {
                            index: event.index,
                            iteration: currentIterationRef.current,
                            type: "tool_result",
                            content: block.summary,
                            toolName: block.name,
                        },
                    ]);
                } else if (block.type === "thinking") {
                    setEventLog((prev) => {
                        const idx = prev.findIndex(
                            (e) =>
                                e.index === event.index && e.type === "thinking"
                        );
                        if (idx === -1) return prev;
                        const updated = [...prev];
                        updated[idx] = {
                            ...updated[idx],
                            content: block.thinking,
                        };
                        return updated;
                    });
                }
                break;
            }

            case "progress": {
                setEventLog((prev) => [
                    ...prev,
                    {
                        index: prev.length,
                        iteration: currentIterationRef.current,
                        type: "progress",
                        content: event.detail,
                        step: event.step,
                    },
                ]);
                break;
            }

            case "refinement_preview": {
                setPendingDiff(event.diff);
                break;
            }

            case "generation_complete": {
                setDraftId(event.draft_id);
                setState("complete");
                break;
            }

            case "generation_error": {
                setError(event.error);
                setState("error");
                break;
            }
        }
    }, []);

    const generate = useCallback(
        async (prompt: string, targets: IChecklistGenerationTargets) => {
            // Cancel any in-flight request before starting a new one
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            setLastPrompt(prompt);
            setLastOrganizationId(targets.organization_id);
            setLastOrganizationGroupId(targets.organization_group_id);
            setEventLog([]);
            setDraftId(null);
            setError(null);
            setErrorStatus(null);
            setState("generating");
            currentIterationRef.current = 0;

            let requestCompleted = false;
            try {
                const stream = await generateChecklist(
                    { prompt, ...targets },
                    controller.signal
                );
                requestCompleted = true;

                let receivedTerminalEvent = false;
                for await (const event of parseNDJSONStream<GenerationEvent>(
                    stream
                )) {
                    if (controller.signal.aborted) break;
                    processEvent(event);
                    if (
                        event.type === "generation_complete" ||
                        event.type === "generation_error"
                    ) {
                        receivedTerminalEvent = true;
                    }
                }

                // Guard against stuck "generating" state when no terminal
                // event was received (e.g. non-JSON response from LB)
                if (!receivedTerminalEvent && !controller.signal.aborted) {
                    setError(
                        "Generation stream ended without a result. Please try again."
                    );
                    setState("error");
                }
            } catch (err) {
                // AbortError / CanceledError — user cancelled, return to idle
                if (
                    err instanceof Error &&
                    (err.name === "AbortError" ||
                        err.name === "CanceledError" ||
                        err.message === "canceled")
                ) {
                    setState("idle");
                    return;
                }

                const message =
                    err instanceof Error
                        ? err.message
                        : "An unexpected error occurred";
                setErrorStatus(
                    !requestCompleted && axios.isAxiosError(err)
                        ? (err.response?.status ?? null)
                        : null
                );
                setError(message);
                setState("error");
            } finally {
                abortControllerRef.current = null;
            }
        },
        [processEvent]
    );

    const refine = useCallback(
        async (currentDraftId: number, refinementPrompt: string) => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();
            abortControllerRef.current = controller;

            setEventLog([]);
            setError(null);
            setErrorStatus(null);
            setPendingDiff(null);
            setState("refining");
            currentIterationRef.current = 0;

            try {
                const stream = await refineChecklist(
                    currentDraftId,
                    refinementPrompt,
                    controller.signal
                );

                let receivedTerminalEvent = false;
                for await (const event of parseNDJSONStream<GenerationEvent>(
                    stream
                )) {
                    if (controller.signal.aborted) break;
                    processEvent(event);
                    if (
                        event.type === "generation_complete" ||
                        event.type === "generation_error"
                    ) {
                        receivedTerminalEvent = true;
                    }
                }

                if (!receivedTerminalEvent && !controller.signal.aborted) {
                    setError(
                        "Refinement stream ended without a result. Please try again."
                    );
                    setState("error");
                }
            } catch (err) {
                if (
                    err instanceof Error &&
                    (err.name === "AbortError" ||
                        err.name === "CanceledError" ||
                        err.message === "canceled")
                ) {
                    setState("complete");
                    return;
                }

                const message =
                    err instanceof Error
                        ? err.message
                        : "An unexpected error occurred";
                setError(message);
                setState("error");
            } finally {
                abortControllerRef.current = null;
            }
        },
        [processEvent]
    );

    const cancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setState("idle");
    }, []);

    const clearPendingDiff = useCallback(() => {
        setPendingDiff(null);
    }, []);

    const reset = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        setEventLog([]);
        setDraftId(null);
        setError(null);
        setErrorStatus(null);
        setPendingDiff(null);
        setState("idle");
        // lastPrompt is intentionally NOT cleared here
    }, []);

    return {
        generate,
        refine,
        cancel,
        reset,
        state,
        eventLog,
        draftId,
        error,
        errorStatus,
        pendingDiff,
        clearPendingDiff,
        lastPrompt,
        lastOrganizationId,
        lastOrganizationGroupId,
    };
};
